import { ChangeDetectionStrategy, Component, HostBinding, ViewEncapsulation, inject } from '@angular/core';

import { I18NEXT_SERVICE } from 'angular-i18next';
import { Observable, map } from 'rxjs';

import { CategorySearch, GlobalProductsRoutes } from '@hdms/shared/common-types';
import { ExtendedAutoSuggestions } from '@hdms/shared/page-frame/data-access';
import { IconsName } from '@hdms/shared/ui-core';

import { SearchResultsComponentAbstract } from '../search-results-component.abstract';

interface GroupedSearchCategories extends CategorySearch {
  children: CategorySearch[];
}

interface GroupedCategoriesPlusTerm extends Pick<ExtendedAutoSuggestions, 'term'> {
  categories: GroupedSearchCategories[];
}

function addCategoryBase(category: string): string {
  return `/${GlobalProductsRoutes.BASE}/${category}`;
}

/**
 * Group categories by their parent category.
 * In case no parent category assigned, add them to fallback "products" category.
 * @param categories
 * @param productCategory
 */
function transformCategories(categories: CategorySearch[], productCategory: CategorySearch): GroupedSearchCategories[] {
  return Object.values(
    categories.reduce((grouped, { id, title, parent }) => {
      const parentId = parent?.id || productCategory.id;
      grouped[parentId] ||= {
        ...(parent ? { ...parent, id: addCategoryBase(parentId) } : productCategory),
        children: []
      };
      grouped[parentId].children.push({ id: addCategoryBase(id), title });
      return grouped;
    }, {})
  );
}

@Component({
  selector: 'shrd-pf-ft-search-categories',
  templateUrl: './search-categories.component.html',
  styleUrls: ['./search-categories.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SearchCategoriesComponent extends SearchResultsComponentAbstract {
  readonly suggestions$: Observable<GroupedCategoriesPlusTerm>;

  readonly iconsSet = IconsName;

  private readonly i18next = inject(I18NEXT_SERVICE);

  private readonly productCategory: CategorySearch = {
    id: `/${GlobalProductsRoutes.BASE_CATEGORY_PAGE}`,
    title: this.i18next.t('header.navigation.products')
  };

  @HostBinding('class.shrd-pf-ft-search-categories') private readonly hostClass = true;

  constructor() {
    super();
    this.suggestions$ = this.getCategories$();
  }

  private getCategories$(): Observable<GroupedCategoriesPlusTerm> {
    return this.getSuggestions$().pipe(
      map(
        ({ categories, term }) =>
          categories?.length && { categories: transformCategories(categories, this.productCategory), term }
      )
    );
  }
}
